import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import Image from '@atlaskit/image';
import { Inline, Pressable, Stack, Text, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type NextBestAction as NextBestActionType } from '@post-office/confluence-next-best-action';
import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { useEffect } from 'react';

import HeartImage from './images/heart.svg';
import { type JSX } from 'react/jsx-runtime';

export type Props = {
	onSideNavClick: (nextBestActionType: NextBestActionType) => string | void | null;
	handleOnDismissed: () => void;
	nextBestActionType: NextBestActionType;
};

const styles = cssMap({
	container: {
		position: 'relative',
		flexGrow: 1,
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
		backgroundColor: token('color.background.accent.blue.subtlest'),
		paddingBlock: token('space.150'),
		paddingInlineEnd: token('space.150'),
		paddingInlineStart: token('space.075'),
	},
	link: {
		color: token('color.link.pressed'),
		fontWeight: token('font.weight.medium'),
		cursor: 'pointer',
		padding: '0',
		backgroundColor: 'transparent',

		'&:hover': {
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
	},
	headerRow: {
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		width: '100%',
	},
	inlineStack: {
		width: '100%',
	},
});

const i18n = defineMessages({
	headerText: {
		id: 'post-office.confluence-resurrected-user-welcome-back.next-best-action.header',
		defaultMessage: 'Welcome back',
		description: 'Title shown when displaying the welcome back nudge for resurrected users.',
	},
	bodyText: {
		id: 'post-office.confluence-resurrected-user-welcome-back.body',
		defaultMessage: 'Catch up on the top discussions and highlights from your team.',
		description: 'Body for confluence side nav welcome back nudge for resurrected users.',
	},
	ctaMessage: {
		id: 'post-office.confluence-resurrected-user-welcome-back.cta',
		defaultMessage: "See what's popular",
		description: 'CTA button text for the welcome back nudge for resurrected users.',
	},
	closeLabel: {
		id: 'post-office.confluence-resurrected-user-welcome-back.close',
		defaultMessage: 'Close',
		description: 'Label for the close icon that is represented by an X',
	},
	imageAltText: {
		id: 'post-office.confluence-resurrected-user-welcome-back.image-alt',
		defaultMessage: 'Two hearts overlapped with each other',
		description: 'Alternate text for picture of two hearts',
	},
});

export const ResurrectedUserWelcomeMessage = ({
	onSideNavClick,
	handleOnDismissed,
	nextBestActionType,
}: Props): JSX.Element => {
	const intl = useIntl();
	const imageAltText = intl.formatMessage(i18n.imageAltText);

	// For PO events
	const { messageInstanceClicked, messageInstanceViewed } = useMessageInstanceEvent();

	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	useEffect(() => {
		if (!messageInstanceViewed) {
			return;
		}

		void messageInstanceViewed();
		fireAnalyticsEvent({
			eventType: 'screen',
			name: 'confluenceResurrectedUserWelcomeSidebarNudge',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleDismissClicked = () => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'resurrected-user-welcome-back-dismiss',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {},
		});

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'resurrectedUserWelcomeBackDismiss',
		});

		// moves to the next message in queue
		void handleOnDismissed();
	};

	const handleCtaClicked = () => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'resurrected-user-welcome-back-cta',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'open',
			},
			additionalMetadataFromFrontend: {},
		});

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'resurrectedUserWelcomeBackCta',
		});

		// moves to the next message in queue and also calls necessary action in confluence
		void onSideNavClick(nextBestActionType);
	};

	return (
		<AnalyticsContext data={{ source: 'confluence-resurrected-user-welcome-back-message' }}>
			<Flex xcss={styles.container} testId="confluence-resurrected-user-welcome-back-message">
				<Inline xcss={styles.inlineStack} shouldWrap={false} space="space.100">
					<Image src={HeartImage} alt={imageAltText} />
					<Stack alignInline="start" grow="fill" space="space.100">
						<Inline xcss={styles.headerRow}>
							<Inline shouldWrap={false} alignBlock="center" grow="fill" spread="space-between">
								<Heading size="xsmall">
									<FormattedMessage {...i18n.headerText} />
								</Heading>
							</Inline>
							<IconButton
								icon={CrossIcon}
								appearance="subtle"
								label={<FormattedMessage {...i18n.closeLabel} />}
								isTooltipDisabled={false}
								onClick={handleDismissClicked}
								spacing="compact"
							/>
						</Inline>
						<Text size="small">
							<FormattedMessage {...i18n.bodyText} />
						</Text>
						<Pressable xcss={styles.link} testId="cta-link" onClick={handleCtaClicked}>
							<FormattedMessage {...i18n.ctaMessage} />
						</Pressable>
					</Stack>
				</Inline>
			</Flex>
		</AnalyticsContext>
	);
};
